/**
 * Informationsmodal om kontrollbalansräkning enligt ABL 25 kap 13§
 *
 * Visas när eget kapital understiger hälften av aktiekapitalet.
 * Förklarar lagkrav och styrelsens skyldigheter.
 */

import { Modal } from "@/components/common/Modal";
import { AlertTriangle, Scale, CheckCircle2 } from "lucide-react";

interface KontrollbalansrakningInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  equity: number;
  shareCapital: number;
}

export function KontrollbalansrakningInfoModal({
  isOpen,
  onClose,
  equity,
  shareCapital,
}: KontrollbalansrakningInfoModalProps) {
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("sv-SE", { style: "decimal" }).format(value);

  const halfShareCapital = shareCapital / 2;
  const deficit = halfShareCapital - equity;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Kontrollbalansräkning" size="lg">
      <div className="space-y-6">
        {/* Varningsbox */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={24} />
            <div>
              <h3 className="font-semibold text-amber-800">
                Eget kapital understiger kritisk nivå
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Eget kapital ({formatCurrency(equity)} kr) är lägre än hälften av
                aktiekapitalet ({formatCurrency(halfShareCapital)} kr).
                Underskottet är {formatCurrency(deficit)} kr.
              </p>
            </div>
          </div>
        </div>

        {/* Lagkrav */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Scale className="text-gray-600" size={20} />
            <h4 className="font-semibold text-gray-900">
              Vad säger lagen? (ABL 25 kap 13-20§)
            </h4>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                1
              </div>
              <div>
                <p className="font-medium text-gray-900">Upprätta kontrollbalansräkning</p>
                <p className="text-gray-600 mt-1">
                  Styrelsen ska <strong>genast</strong> upprätta en kontrollbalansräkning
                  när det finns skäl att anta att bolagets eget kapital understiger hälften
                  av aktiekapitalet.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                2
              </div>
              <div>
                <p className="font-medium text-gray-900">Granskning av revisor</p>
                <p className="text-gray-600 mt-1">
                  Om bolaget har revisor ska denne granska kontrollbalansräkningen
                  inom två veckor.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                3
              </div>
              <div>
                <p className="font-medium text-gray-900">Första kontrollstämma</p>
                <p className="text-gray-600 mt-1">
                  Om kontrollbalansräkningen visar kapitalbrist ska styrelsen kalla
                  till bolagsstämma (första kontrollstämman) för att pröva om bolaget
                  ska träda i likvidation.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                4
              </div>
              <div>
                <p className="font-medium text-gray-900">Åtta månaders rådrum</p>
                <p className="text-gray-600 mt-1">
                  Om stämman beslutar att bolaget ska fortsätta driva verksamheten
                  har styrelsen åtta månader på sig att läka kapitalbristen.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                5
              </div>
              <div>
                <p className="font-medium text-gray-900">Andra kontrollstämma</p>
                <p className="text-gray-600 mt-1">
                  Inom åtta månader ska en ny kontrollbalansräkning upprättas och
                  en andra kontrollstämma hållas. Om kapitalbristen kvarstår ska
                  bolaget träda i likvidation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Varning om personligt ansvar */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="font-semibold text-red-800">Personligt ansvar</h4>
              <p className="text-sm text-red-700 mt-1">
                Om styrelsen inte följer reglerna kan styrelseledamöterna bli
                <strong> personligt betalningsansvariga</strong> för bolagets
                skulder som uppkommer efter att skyldigheten att upprätta
                kontrollbalansräkning uppstod.
              </p>
            </div>
          </div>
        </div>

        {/* Rekommendationer */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="text-green-600" size={20} />
            <h4 className="font-semibold text-gray-900">Rekommenderade åtgärder</h4>
          </div>

          <ul className="list-disc list-inside text-sm text-gray-700 space-y-2 ml-1">
            <li>Kontakta er revisor eller redovisningskonsult omgående</li>
            <li>Låt en revisor granska kontrollbalansräkningen</li>
            <li>Dokumentera styrelsens beslut och åtgärder noggrant</li>
            <li>
              Överväg åtgärder för att återställa kapitalet (t.ex. aktieägartillskott,
              nyemission, eller minskning av aktiekapitalet)
            </li>
          </ul>
        </div>

        {/* Stäng-knapp */}
        <div className="flex justify-end pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Jag förstår
          </button>
        </div>
      </div>
    </Modal>
  );
}
